import React from "react";
import { toast } from "react-toastify";
import {
	Control,
	Controller,
	ControllerFieldState,
	ControllerRenderProps,
	FieldPath,
	FieldValues,
	UseControllerProps,
	UseFormStateReturn,
	useFormState,
	useWatch,
} from "react-hook-form";

import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import KogInputText from "../inputText/inputText";
import * as S from "./styles";

type IConfigRange = {
	rangeNumbers: { min: number; max: number };
	decimalPlaces?: number;
};
type IHTMLInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "number" | "color" | "size">;
interface IKogInputRangeAmountControlledProps<T extends FieldValues> {
	idScreen: string;
	nameInitial: FieldPath<T>;
	nameFinal: FieldPath<T>;
	configRange?: Partial<IConfigRange>;
	customInputProps?: IHTMLInputProps;
	onChangeCustom?: (value: string) => void;
}
const INITIAL_CONFIG_RANGE: IConfigRange = {
	rangeNumbers: { min: 0, max: 100 },
	decimalPlaces: 2,
};
/**
 * Component to render a controlled input range
 *
 * @param {string} idScreen - Id of the screen
 * @param {string} nameInitial - Name of the first input
 * @param {string} nameFinal - Name of the second input
 * @param {Control<FieldValues>} control - Control of the form
 * @param {React.InputHTMLAttributes<HTMLInputElement>} customInputProps - Custom props of the input
 * @param {Function} onChangeCustom - Function to be executed when the value changes
 * @example
 * const { control } = useForm(); //react-hoook-form
 *
 *  <KogInputRangeAmountControlled
 *    id='inputtest'
 *    name="inputtest"
 *    control={control}
 *  />
 */
function KogInputRangeAmountControlled<TFieldValues extends FieldValues>({
	idScreen,
	nameInitial,
	nameFinal,
	control,
	configRange: configRangeProps = {},
	customInputProps = {},
	onChangeCustom = () => {},
}: Required<Pick<UseControllerProps<TFieldValues>, "control">> & IKogInputRangeAmountControlledProps<TFieldValues>) {
	const configRange = { ...INITIAL_CONFIG_RANGE, ...configRangeProps };
	const { errors, isSubmitting } = useFormState({
		control,
	});
	const watcherNameFinal = useWatch({
		control,
		name: nameFinal,
	}) as string;
	const propsInput = {
		[nameInitial]: {
			placeholder: getTranslateByKey("information.initialTotalLoss"),
		},
		[nameFinal]: {
			placeholder: getTranslateByKey("information.finalTotalLoss"),
		},
	};
	function maskPercentage(value: string, oldValue: string) {
		const separator = value.includes(",") ? "," : ".";
		const newValue = value.replace(/[^0-9,.]/g, "");
		const valueNumber = parseFloat(newValue);
		const { rangeNumbers, decimalPlaces } = configRange;

		// #region Validations
		const objValidateValue = {
			exceedMax: valueNumber > rangeNumbers.max,
			exceedMin: valueNumber < rangeNumbers.min,
			wrong2Separators: newValue.split(separator).length > 2,
			have2CommasDot: newValue.split(",").length > 1 && newValue.split(".").length > 1,
			wrongDecimalPlaces:
				decimalPlaces && newValue.split(separator)[1] && newValue.split(separator)[1].length > decimalPlaces,
		};
		const haveError = Object.values(objValidateValue).find((value) => value);
		if (objValidateValue.exceedMax) return rangeNumbers.max.toString();
		if (objValidateValue.exceedMin) return rangeNumbers.min.toString();
		if (haveError) return oldValue;
		//#endregion

		return newValue;
	}

	const renderInput = ({
		field,
		fieldState: { error },
	}: {
		field: ControllerRenderProps<FieldValues, string>;
		fieldState: ControllerFieldState;
		formState: UseFormStateReturn<FieldValues>;
	}) => {
		const validateValue = field.value;
		const idScreenInput = `${idScreen}-txt-${field.name}`;

		const onChangeInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const oldValue = field.value;
			const validateValue = maskPercentage(e.target.value, oldValue);

			field.onChange(validateValue);
			onChangeCustom(validateValue);
		};

		const baseInputProps: IHTMLInputProps = {
			...field,
			...customInputProps,
			id: idScreenInput,
			value: validateValue,
			onChange: onChangeInput,
		};

		return (
			<S.InpuTWithSuffix>
				<KogInputText
					hasOnlyNumber
					autoComplete="off"
					type="number"
					placeholder={propsInput[field.name].placeholder}
					error={!!error}
					{...baseInputProps}
				/>
			</S.InpuTWithSuffix>
		);
	};
	const VALIDATE_RANGE_DEFAULT: Record<string, (value: string) => boolean | string> = {
		min: (value: string) => Number(value) >= configRange.rangeNumbers.min,
		max: (value: string) => Number(value) <= configRange.rangeNumbers.max,
	};
	const VALIDATE_RANGE_INTIIAL: Record<string, (value: string) => boolean | string> = {
		...VALIDATE_RANGE_DEFAULT,
		maxFinal: (value: string) => {
			if (watcherNameFinal !== "") {
				const isValid = Number(value) <= Number(watcherNameFinal);

				if (!isValid && isSubmitting) {
					toast.error(getTranslateByKey("information.initialValueMustBeFinalValue", true));
				}

				return isValid;
			}
			return true;
		},
	};
	const haveError = !isNullUndefined(errors[nameInitial]) || !isNullUndefined(errors[nameFinal]);
	const messageError = errors[nameInitial]?.message || errors[nameFinal]?.message;

	return (
		<S.Container>
			<Controller
				name={nameInitial}
				control={control as Control<FieldValues>}
				rules={{
					validate: VALIDATE_RANGE_INTIIAL,
				}}
				render={renderInput}
			/>
			<S.TextFromTo>{getTranslateByKey("information.toField")}</S.TextFromTo>
			<Controller
				name={nameFinal}
				control={control as Control<FieldValues>}
				rules={{
					validate: VALIDATE_RANGE_DEFAULT,
				}}
				render={renderInput}
			/>
			{haveError && <S.TextMessageError>{messageError}</S.TextMessageError>}
		</S.Container>
	);
}

export default KogInputRangeAmountControlled;
